import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  UserCheck,
  Edit,
  Trash2,
  Eye,
  Star,
  Users,
  TrendingUp,
  Award,
  Clock,
  Phone,
  Mail,
  MoreVertical,
  Store,
  Wallet
} from 'lucide-react';
import { Technician, DarkStore } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const TechniciansPage: React.FC = () => {
  const navigate = useNavigate();
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showActions, setShowActions] = useState<number | null>(null);
  const [darkStores, setDarkStores] = useState<DarkStore[]>([]);
  const [updatingDarkStore, setUpdatingDarkStore] = useState<number | null>(null);

  useEffect(() => {
    loadTechnicians();
    loadDarkStores();
  }, []);

  const loadDarkStores = async () => {
    try {
      const data = await apiService.getDarkStores();
      setDarkStores(Array.isArray(data) ? data : (data?.entity ?? []));
    } catch {
      // non-critical, ignore
    }
  };

  const loadTechnicians = async () => {
    setLoading(true);
    try {
      const response = await apiService.getTechnicians();
      if (response.isSuccess && response.entity) {
        setTechnicians(response.entity || []);
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to load technicians');
        setTechnicians([]);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
      toast.error('Error connecting to server. Please check if the backend is running.');
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDarkStoreChange = async (technicianId: number, darkStoreId: number | null) => {
    setUpdatingDarkStore(technicianId);
    try {
      const response = await apiService.assignTechnicianDarkStore(technicianId, darkStoreId);
      if (response.isSuccess) {
        const store = darkStores.find(s => s.id === darkStoreId);
        setTechnicians(prev => prev.map(t =>
          t.id === technicianId
            ? { ...t, darkStoreId, darkStoreName: store?.name ?? null }
            : t
        ));
        toast.success('Dark store updated');
      } else {
        toast.error('Failed to update dark store');
      }
    } catch {
      toast.error('Error updating dark store');
    } finally {
      setUpdatingDarkStore(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-red-100 text-red-800',
      'On Leave': 'bg-yellow-100 text-yellow-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getRatingStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-4 w-4 fill-yellow-400 text-yellow-400 opacity-50" />);
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }

    return stars;
  };

  const calculateCompletionRate = (completed: number, total: number) => {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const filteredTechnicians = technicians.filter(tech =>
    tech.name.toLowerCase().includes(search.toLowerCase()) ||
    tech.email.toLowerCase().includes(search.toLowerCase()) ||
    tech.mobile.includes(search) ||
    tech.specialization.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <UserCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Technicians</h1>
            <p className="text-sm text-gray-500">Manage your service technicians</p>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/technicians/create')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg hover:from-emerald-700 hover:to-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Technician
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search Technicians</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, mobile, or specialization..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="text-sm text-gray-500">
            {filteredTechnicians.length} technician{filteredTechnicians.length !== 1 ? 's' : ''} found
          </div>
        </div>
      </div>

      {/* Technicians Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : filteredTechnicians.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No technicians found</h3>
            <p className="text-gray-500">Add your first technician to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Technician</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Specialization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dark Store</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTechnicians.map((technician) => (
                  <tr key={technician.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {technician.imagePath ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={technician.imagePath}
                              alt={technician.name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {technician.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{technician.name}</div>
                          <div className="text-sm text-gray-500">{technician.experience} years exp</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Phone className="h-3 w-3 mr-1 text-gray-400" />
                          {technician.mobile}
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 mr-1 text-gray-400" />
                          {technician.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{technician.specialization}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(technician.status)}`}>
                        {technician.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                      {updatingDarkStore === technician.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600" />
                      ) : (
                        <select
                          value={technician.darkStoreId ?? ''}
                          onChange={e => handleDarkStoreChange(technician.id, e.target.value ? Number(e.target.value) : null)}
                          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-full"
                        >
                          <option value="">— Unassigned —</option>
                          {darkStores.map(store => (
                            <option key={store.id} value={store.id}>{store.name}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="flex items-center mb-1">
                          <Users className="h-3 w-3 mr-1 text-gray-400" />
                          <span className="text-gray-900">{technician.completedOrders}/{technician.totalOrders}</span>
                          <span className="text-gray-500 ml-1">
                            ({calculateCompletionRate(technician.completedOrders, technician.totalOrders)}%)
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 mr-1 text-gray-400" />
                          <span className="text-gray-500">{technician.averageCompletionTime}h avg</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex mr-2">
                          {getRatingStars(technician.rating)}
                        </div>
                        <span className="text-sm text-gray-900">{technician.rating}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="relative">
                        <button
                          onClick={() => setShowActions(showActions === technician.id ? null : technician.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        
                        {showActions === technician.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  navigate(`/technicians/edit/${technician.id}`);
                                  setShowActions(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Technician
                              </button>
                              <button
                                onClick={() => {
                                  navigate(`/technicians/edit/${technician.id}`);
                                  setShowActions(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Wallet className="h-4 w-4 mr-2" />
                                View Wallet
                              </button>
                              <button
                                onClick={() => {
                                  toast.success('Performance view coming soon');
                                  setShowActions(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <TrendingUp className="h-4 w-4 mr-2" />
                                View Performance
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm(`Are you sure you want to delete ${technician.name}?`)) {
                                    try {
                                      const response = await apiService.deleteTechnician(technician.id);
                                      if (response.isSuccess) {
                                        toast.success('Technician deleted successfully');
                                        loadTechnicians();
                                      } else {
                                        toast.error(response.errors?.join(', ') || 'Failed to delete technician');
                                      }
                                    } catch (error) {
                                      console.error('Error deleting technician:', error);
                                      toast.error('Error deleting technician');
                                    }
                                  }
                                  setShowActions(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Technician
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechniciansPage; 